import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Loader2, Truck, Activity, Fuel, FileBadge, AlertTriangle, Wallet, Users, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface OpsLive {
  timestamp: string;
  trips: { inProgress: number; completedToday: number; plannedToday: number };
  vehicles: { total: number; enginesOn: number; movingNow: number; idleNow: number; parkedToday: number; needAttention: number };
  drivers: { activeToday: number; totalActive: number };
  fuel: { transactionsToday: number; sarToday: number; litersToday: number; suspectedWaste: number };
  documents: { expiringIn7Days: number; expiringIn30Days: number; expired: number };
  anomalies: { firedToday: number; openCritical: number; openHigh: number };
  money: { revenueAccrued: number; fuelCost: number; expenses: number; wasteCost: number; costAccrued: number; netToday: number };
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function OperationsLivePage() {
  const [data, setData] = useState<OpsLive | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [tickSec, setTickSec] = useState(0);

  const fetchNow = useCallback(async () => {
    try {
      const r = await apiClient.get<OpsLive>('/api/v1/dashboard/operations-live');
      setData(r.data);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNow(); }, [fetchNow]);

  // Auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(fetchNow, 30000);
    return () => clearInterval(id);
  }, [fetchNow]);

  // Visible "Xs ago" tick
  useEffect(() => {
    const id = setInterval(() => setTickSec((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ago = updatedAt ? Math.floor((Date.now() - updatedAt.getTime()) / 1000) : 0;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2">
            <Activity className="h-4 w-4" /> Operations Live
            {updatedAt && (
              <span className="text-xs text-muted-foreground font-normal">· {ago}s ago</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchNow} className="gap-2 h-8">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {loading && !data ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : data ? (
          <>
            {/* Money meter centerpiece */}
            <div className={'rounded-xl border p-6 text-center ' + (data.money.netToday >= 0 ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5')}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Live profit today</div>
              <div className={'text-5xl font-display mt-2 ' + (data.money.netToday >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                {data.money.netToday >= 0 ? <TrendingUp className="inline h-8 w-8 mb-1 me-2" /> : <TrendingDown className="inline h-8 w-8 mb-1 me-2" />}
                {fmtSar(data.money.netToday)}
              </div>
              <div className="text-xs text-muted-foreground mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                <span>Revenue {fmtSar(data.money.revenueAccrued)}</span>
                <span>Fuel −{fmtSar(data.money.fuelCost)}</span>
                <span>Expenses −{fmtSar(data.money.expenses)}</span>
                {data.money.wasteCost > 0 && <span className="text-amber-700 dark:text-amber-400">Waste −{fmtSar(data.money.wasteCost)}</span>}
              </div>
            </div>

            {/* Category grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Group title="Trips" icon={<Activity className="h-4 w-4" />}>
                <Stat label="In progress" value={String(data.trips.inProgress)} />
                <Stat label="Today done"  value={String(data.trips.completedToday)} />
                <Stat label="Planned"     value={String(data.trips.plannedToday)} />
              </Group>

              <Group title="Fleet" icon={<Truck className="h-4 w-4" />}>
                <Stat label="Engines on"  value={data.vehicles.enginesOn + ' / ' + data.vehicles.total} />
                <Stat label="Moving"      value={String(data.vehicles.movingNow)} />
                <Stat label="Idle now"    value={String(data.vehicles.idleNow)} />
                <Stat label="Parked"      value={String(data.vehicles.parkedToday)} />
                {data.vehicles.needAttention > 0 && (
                  <Stat label="Attention" value={String(data.vehicles.needAttention)} tone="bad" />
                )}
              </Group>

              <Group title="Drivers" icon={<Users className="h-4 w-4" />}>
                <Stat label="Active today" value={data.drivers.activeToday + ' / ' + data.drivers.totalActive} />
              </Group>

              <Group title="Fuel" icon={<Fuel className="h-4 w-4" />}>
                <Stat label="Tx today" value={String(data.fuel.transactionsToday)} />
                <Stat label="SAR"      value={fmtSar(data.fuel.sarToday)} />
                <Stat label="Liters"   value={data.fuel.litersToday.toFixed(0) + ' L'} />
                {data.fuel.suspectedWaste > 0 && (
                  <Stat label="Waste cost" value={fmtSar(data.fuel.suspectedWaste)} tone="bad" />
                )}
              </Group>

              <Group title="Documents" icon={<FileBadge className="h-4 w-4" />}>
                {data.documents.expired > 0 && <Stat label="Expired" value={String(data.documents.expired)} tone="bad" />}
                <Stat label="In 7 days"  value={String(data.documents.expiringIn7Days)} />
                <Stat label="In 30 days" value={String(data.documents.expiringIn30Days)} />
              </Group>

              <Group title="Anomalies" icon={<AlertTriangle className="h-4 w-4" />}>
                <Stat label="Fired today"   value={String(data.anomalies.firedToday)} />
                {data.anomalies.openCritical > 0 && <Stat label="Open critical" value={String(data.anomalies.openCritical)} tone="bad" />}
                {data.anomalies.openHigh > 0     && <Stat label="Open high"     value={String(data.anomalies.openHigh)}     tone="warn" />}
              </Group>

              <Group title="Money — today" icon={<Wallet className="h-4 w-4" />}>
                <Stat label="Revenue" value={fmtSar(data.money.revenueAccrued)} />
                <Stat label="Cost"    value={fmtSar(data.money.costAccrued)} tone={data.money.costAccrued > data.money.revenueAccrued ? 'bad' : undefined} />
                <Stat label="Net"     value={fmtSar(data.money.netToday)}    tone={data.money.netToday >= 0 ? 'good' : 'bad'} />
              </Group>
            </div>

            {/* hidden re-render trigger so the 'Xs ago' counter ticks */}
            <span className="hidden">{tickSec}</span>
          </>
        ) : null}
      </main>
    </div>
  );
}

function Group({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5 mb-2">
        {icon} {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | 'warn' }) {
  const cls =
    tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' :
    tone === 'bad'  ? 'text-rose-600 dark:text-rose-400' :
    tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={'font-mono ' + cls}>{value}</span>
    </div>
  );
}
